/**
 * HR "Apps & Training" (2026-10-07, Manak) — data for the Mobile Apps page, the
 * App Tour Progress page and their two preview boxes on the People Desk.
 *
 * Backend: GET /hr/app-overview/releases and /hr/app-overview/tour-progress
 * (HR + admin only). The server sends raw rows; the % is worked out here with
 * the SAME rule the person's own App Tour uses (tourCatalog.canSeeLesson):
 *
 *   lessons for a person = every lesson in the catalog (phone AND web) that
 *   their App Tour lists for them:
 *     admin / owner / super_admin → all lessons (incl. the admin-only phone setup)
 *     senior sales manager (P01) and coordinator (P02) → all but the admin-only ones
 *     everyone else → the lessons marked for all staff (no manager-only, no admin-only)
 *   done = their ticked lessons that are in that set. Ticks for lessons outside
 *   their set (or ids no longer in the catalog) are not counted.
 */
import { z } from 'zod';
import { useProfileResource } from '@/pages/dashboard/roles/useProfileResource';
import { TOUR_LESSONS } from '@/pages/tour/tourCatalog.generated';
import { canSeeLesson, tourAudience, type TourAudience } from '@/pages/tour/tourCatalog';
import type { TourLesson } from '@/pages/tour/tourTypes';

const isoDate = z.string().refine((v) => Number.isFinite(Date.parse(v)));

const appSchema = z.object({
  key: z.string(),
  name: z.string(),
  versionName: z.string(),
  versionCode: z.number(),
  forced: z.boolean(),
  apkUrl: z.string(),
  releasedAt: isoDate.nullable(),
});
export type MobileApp = z.infer<typeof appSchema>;
const releasesSchema = z.object({ apps: z.array(appSchema) });

const employeeSchema = z.object({
  userId: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  designation: z.string().nullable(),
  team: z.string().nullable(),
  done: z.array(z.object({ lessonId: z.string(), doneAt: isoDate.nullable() })),
  appVersion: z.string().nullable(),
  appVersionSeenAt: isoDate.nullable(),
});
export type TourEmployee = z.infer<typeof employeeSchema>;
const progressSchema = z.object({ employees: z.array(employeeSchema) });

const parseReleases = (b: any) => releasesSchema.parse(b?.data).apps;
const parseProgress = (b: any) => progressSchema.parse(b?.data).employees;
export const useMobileApps = () => useProfileResource('/hr/app-overview/releases', parseReleases);
export const useTourProgress = () => useProfileResource('/hr/app-overview/tour-progress', parseProgress);

/** '8.14.197+81597' → { name: '8.14.197', code: 81597 }. Anything else keeps its text, code null. */
export function parseAppVersion(raw: string | null): { name: string; code: number | null } | null {
  if (!raw || !raw.trim()) return null;
  const [name, build] = raw.trim().split('+');
  const code = build && /^\d+$/.test(build) ? Number(build) : null;
  return { name: name || raw.trim(), code };
}

export interface TourProgressRow {
  userId: string;
  name: string;
  role: string | null;
  roleLabel: string;
  team: string | null;
  audience: TourAudience;
  done: number;
  total: number;
  /** 0–100, whole number, floored so 99.6% never reads as finished. */
  pct: number;
  last: { lessonId: string; title: string; doneAt: string | null } | null;
  app: { name: string; code: number | null; old: boolean } | null;
}

export function roleLabel(role: string | null): string {
  if (!role) return 'No role';
  return role.replace(/[_-]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

const LESSONS_BY_AUDIENCE = new Map<TourAudience, Set<string>>();
function lessonsFor(audience: TourAudience, lessons: TourLesson[]): Set<string> {
  if (lessons === TOUR_LESSONS && LESSONS_BY_AUDIENCE.has(audience)) return LESSONS_BY_AUDIENCE.get(audience)!;
  const set = new Set(lessons.filter((l) => canSeeLesson(l, audience)).map((l) => l.id));
  if (lessons === TOUR_LESSONS) LESSONS_BY_AUDIENCE.set(audience, set);
  return set;
}

export function tourProgressRows(
  employees: TourEmployee[],
  salesLatestCode: number | null,
  lessons: TourLesson[] = TOUR_LESSONS,
): TourProgressRow[] {
  const byId = new Map(lessons.map((l) => [l.id, l]));
  return employees.map((e) => {
    const audience = tourAudience({ role: e.role ? { name: e.role } : null, designation: e.designation });
    const mine = lessonsFor(audience, lessons);
    const counted = e.done.filter((d) => mine.has(d.lessonId));
    const uniq = new Map(counted.map((d) => [d.lessonId, d]));
    const total = mine.size;
    const doneCount = uniq.size;
    const latest = [...uniq.values()].sort((a, b) => (Date.parse(b.doneAt ?? '') || 0) - (Date.parse(a.doneAt ?? '') || 0))[0];
    const v = parseAppVersion(e.appVersion);
    return {
      userId: e.userId,
      name: e.name,
      role: e.role,
      roleLabel: roleLabel(e.role),
      team: e.team,
      audience,
      done: doneCount,
      total,
      pct: total ? Math.floor((doneCount * 100) / total) : 0,
      last: latest ? { lessonId: latest.lessonId, title: byId.get(latest.lessonId)?.title ?? latest.lessonId, doneAt: latest.doneAt } : null,
      app: v ? { ...v, old: salesLatestCode != null && v.code != null && v.code < salesLatestCode } : null,
    };
  });
}

export interface TourTotals { employees: number; started: number; finished: number; notStarted: number }
export function tourTotals(rows: TourProgressRow[]): TourTotals {
  const started = rows.filter((r) => r.done > 0).length;
  return {
    employees: rows.length,
    started,
    finished: rows.filter((r) => r.total > 0 && r.done >= r.total).length,
    notStarted: rows.length - started,
  };
}

export type ProgressFilter = 'all' | 'not-started' | 'below-50' | 'finished';
export function filterRows(rows: TourProgressRow[], f: { progress: ProgressFilter; role: string; team: string }): TourProgressRow[] {
  return rows.filter((r) => {
    if (f.role && (r.role ?? '') !== f.role) return false;
    if (f.team && (r.team ?? '') !== f.team) return false;
    if (f.progress === 'not-started') return r.done === 0;
    if (f.progress === 'below-50') return r.pct < 50;
    if (f.progress === 'finished') return r.total > 0 && r.done >= r.total;
    return true;
  });
}

/** Lowest progress first; ties by name. */
export function lowestFirst(rows: TourProgressRow[]): TourProgressRow[] {
  return [...rows].sort((a, b) => a.pct - b.pct || a.done - b.done || a.name.localeCompare(b.name));
}

export const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }) : '';

/** Rows for the Excel download, in the order shown on screen. */
export function excelRows(rows: TourProgressRow[]) {
  return rows.map((r) => ({
    Employee: r.name,
    Role: r.roleLabel,
    Team: r.team ?? '',
    'Progress %': r.pct,
    'Lessons done': r.done,
    'Lessons for role': r.total,
    'Last lesson watched': r.last ? `${r.last.lessonId} ${r.last.title}` : '',
    'Last watched on': r.last?.doneAt ? shortDate(r.last.doneAt) : '',
    'AURA Sales version': r.app ? `${r.app.name}${r.app.old ? ' (old)' : ''}` : '',
  }));
}

export const APP_VERSION_UNKNOWN_HINT =
  'Shows after the AURA Sales app on this person\'s phone sends its version. Only phones that share location send it today.';
